import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth } from "../firebase/config";
import { updateProfile } from "firebase/auth";
import { ref, onValue } from "firebase/database";
import { realtimeDb } from "../firebase/config";
import { updateUserProfile } from "../firebase/userDb";
import {
  FaUser,
  FaShoppingBag,
  FaHeart,
  FaCog,
  FaSignOutAlt,
  FaEdit,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import ShipmentTracker from "../components/ShipmentTracker";

import "./dashboard.css";
import { setPageMeta } from "../utils/seo";



// Simple fallback avatar function
const getFallbackAvatar = (user) => {
  if (!user) return '?';
  
  if (user.displayName && user.displayName.length > 0) {
    return user.displayName[0].toUpperCase();
  }
  
  if (user.email && user.email.length > 0) {
    return user.email[0].toUpperCase();
  }
  
  if (user.phoneNumber && user.phoneNumber.length > 0) {
    return user.phoneNumber[0];
  }
  
  return '?';
};

// Check if URL is a Google profile image (which often gets blocked)
const isGoogleProfileImage = (url) => {
  return url && url.includes('googleusercontent.com');
};

// Custom hook to handle profile image loading with fallback
const useProfileImage = (user) => {
  const [imageState, setImageState] = useState({
    isLoading: false,
    hasError: false,
    shouldShowPlaceholder: !user?.photoURL,
    imageUrl: null,
    fallbackText: getFallbackAvatar(user)
  });

  useEffect(() => {
    if (!user) {
      setImageState({
        isLoading: false,
        hasError: false,
        shouldShowPlaceholder: true,
        imageUrl: null,
        fallbackText: '?'
      });
      return;
    }

    setImageState(prev => ({
      ...prev,
      isLoading: true,
      hasError: false,
      shouldShowPlaceholder: false
    }));

    // Handle profile image loading with tracking prevention awareness
    if (user.photoURL) {
      // Skip Google profile images to avoid tracking prevention errors
      if (isGoogleProfileImage(user.photoURL)) {
        setImageState({
          isLoading: false,
          hasError: false,
          shouldShowPlaceholder: true,
          imageUrl: null,
          fallbackText: getFallbackAvatar(user)
        });
        return;
      }
      
      // For non-Google images, try to load normally
      const img = new Image();
      img.onload = () => {
        setImageState({
          isLoading: false,
          hasError: false,
          shouldShowPlaceholder: false,
          imageUrl: user.photoURL,
          fallbackText: getFallbackAvatar(user)
        });
      };
      img.onerror = () => {
        setImageState({
          isLoading: false,
          hasError: true,
          shouldShowPlaceholder: true,
          imageUrl: null,
          fallbackText: getFallbackAvatar(user)
        });
      };
      img.src = user.photoURL;
    } else {
      setImageState({
        isLoading: false,
        hasError: false,
        shouldShowPlaceholder: true,
        imageUrl: null,
        fallbackText: getFallbackAvatar(user)
      });
    }
  }, [user]);

  return imageState;
};

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const getTabFromQuery = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get("tab") || "profile";
  }, [location.search]);

  
  const [activeTab, setActiveTab] = useState(getTabFromQuery());
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState("all");

  useEffect(() => {
    setPageMeta({
      title: "Dashboard | Banat India",
      description: "",
      keywords: "",
      robots: "noindex, nofollow",
    });
  }, []);
  
  // Use custom hook for profile image handling
  const profileImageState = useProfileImage(user);

  // Update activeTab if the URL changes
  useEffect(() => {
    setActiveTab(getTabFromQuery());
  }, [getTabFromQuery]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUser(user);
        setEditedName(user.displayName || "");
      } else {
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    const ordersRef = ref(realtimeDb, `orders/${user.uid}`);
    const unsubscribe = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // Convert to array with keys
        const ordersWithKeys = Object.entries(data).map(([key, order]) => ({
          key,
          ...order
        }));
        setOrders(ordersWithKeys);
      } else {
        setOrders([]);
      }
    });
    return () => unsubscribe();
  }, [user]);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (error) {
      try {
        navigate("/");
      } catch {
        window.location.href = "/";
      }
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/dashboard?tab=${tab}`);
  };

  const handleWishlistClick = () => {
    navigate("/wishlist");
  };

  // Function to fetch and update latest order details including AWB
  const fetchAndUpdateOrderDetails = async (orderId, orderKey) => {
    try {
      const currentOrder = orders.find(o => o.key === orderKey);
      const shipmentId = currentOrder?.shiprocket_shipment_id;
      
      if (!shipmentId) {
        alert('No shipment ID found for this order. Please wait for shipment creation.');
        return;
      }
      
      const response = await fetch(`https://shiprocket-backend-maeh.onrender.com/track-shipment/${shipmentId}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        const shipmentTrack = result.data.tracking_data?.shipment_track?.[0];
        const awbNumber = shipmentTrack?.awb_code;
        const courierName = shipmentTrack?.courier_name;
        const status = shipmentTrack?.current_status;
        
        if (user?.uid && awbNumber) {
          const { ref, update } = await import("firebase/database");
          const { realtimeDb } = await import("../firebase/config");
          
          const updateData = {
            shiprocket_awb: awbNumber,
            shiprocket_courier: courierName,
            shiprocket_status: status
          };
          
          await update(ref(realtimeDb, `orders/${user.uid}/${orderKey}`), updateData);
          alert(`✅ Order updated! AWB Number: ${awbNumber}\nCourier: ${courierName}\nStatus: ${status}`);
        } else {
          alert('AWB number not yet generated. Please try again later.');
        }
      } else {
        alert(`Failed to fetch tracking details: ${result.error}`);
      }
    } catch (error) {
      alert(`Error fetching order details: ${error.message}`);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedName(user.displayName || "");
    setUpdateError("");
  };

  const handleSaveClick = async () => {
    try {
      await updateProfile(user, { displayName: editedName });
      await updateUserProfile(user.uid, { displayName: editedName });
      setIsEditing(false);
      setUpdateError("");
    } catch {
      setUpdateError("Failed to update profile. Please try again.");
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedName(user.displayName || "");
    setUpdateError("");
  };

  if (!user) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-sidebar">
        <div className="user-info">
          <div className="user-avatar">
            {profileImageState.imageUrl && !profileImageState.shouldShowPlaceholder ? (
              <img 
                src={profileImageState.imageUrl} 
                alt="Profile" 
                crossOrigin="anonymous"
                loading="lazy"
                onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                  onLoad={(e) => {
                    const placeholder = e.target.nextSibling;
                    if (placeholder && placeholder.classList.contains('avatar-placeholder')) {
                      placeholder.style.display = 'none';
                    }
                  }}
              />
            ) : null}
            <div 
              className="avatar-placeholder"
              style={{ display: profileImageState.shouldShowPlaceholder ? 'flex' : 'none' }}
            >
              {profileImageState.isLoading ? (
                <div className="avatar-loading">⏳</div>
              ) : (
                <div className="avatar-content">
                  {profileImageState.fallbackText}
                  {profileImageState.error && profileImageState.error.includes('429') && (
                    <div className="rate-limit-indicator" title="Profile image temporarily unavailable due to rate limiting">
                      ⚠️
                    </div>
                  )}
                  {profileImageState.isAlternative && (
                    <div className="alternative-indicator" title="Using alternative image URL">
                      🔄
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <h3>{user.displayName || user.email}</h3>
        </div>
        <nav className="dashboard-nav">
          <button
            className={`nav-item ${activeTab === "profile" ? "active" : ""}`}
            onClick={() => handleTabChange("profile")}
          >
            <FaUser /> Profile
          </button>
          <button
            className={`nav-item ${activeTab === "orders" ? "active" : ""}`}
            onClick={() => handleTabChange("orders")}
          >
            <FaShoppingBag /> Orders
          </button>
          <button
            className={`nav-item ${activeTab === "wishlist" ? "active" : ""}`}
            onClick={() => handleWishlistClick()}
          >
            <FaHeart /> Wishlist
          </button>
          <button
            className={`nav-item ${activeTab === "settings" ? "active" : ""}`}
            onClick={() => handleTabChange("settings")}
          >
            <FaCog /> Settings
          </button>
          <button 
            className="nav-item sign-out" 
            onClick={handleSignOut}
          >
            <FaSignOutAlt /> Sign Out
          </button>
        </nav>
      </div>

      <div className="dashboard-content">
        {activeTab === "profile" && (
          <div className="profile-section">
            <h2>Profile Information</h2>
            <div className="profile-details">
              <div className="detail-item">
                <label>Name:</label>
                {isEditing ? (
                  <div className="edit-name">
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      placeholder="Enter your name"
                    />
                    <button onClick={handleSaveClick} className="save-btn">
                      <FaCheck />
                    </button>
                    <button onClick={handleCancelEdit} className="cancel-btn">
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <div className="name-display">
                    <span>{user.displayName || "Not set"}</span>
                    <button onClick={handleEditClick} className="edit-btn">
                      <FaEdit />
                    </button>
                  </div>
                )}
                {updateError && <div className="error-message">{updateError}</div>}
              </div>
              <div className="detail-item">
                <label>Email:</label>
                <span>{user.email}</span>
              </div>
              <div className="detail-item">
                <label>Phone:</label>
                <span>{user.phoneNumber || "Not set"}</span>
              </div>
              <div className="detail-item">
                <label>Account Created:</label>
                <span>{user.metadata?.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : "Unknown"}</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === "orders" && (
          <div className="orders-section">
            <h2 data-count={`${orders.length} orders`}>My Orders</h2>
            <div className="order-filter">
              <select 
                value={orderFilter} 
                onChange={(e) => setOrderFilter(e.target.value)}
                aria-label="Filter orders by status"
              >
                <option value="all">All Orders</option>
                <option value="paid">Paid Orders</option>
                <option value="pending">Pending Orders</option>
                <option value="delivered">Delivered Orders</option>
                <option value="failed">Failed Orders</option>
                <option value="cancelled">Cancelled Orders</option>
              </select>
            </div>
            <div className="orders-list">
              {orders.length === 0 ? (
                <div className="no-orders">
                  <FaShoppingBag />
                  <p>No orders found.</p>
                  <p style={{fontSize: '0.9rem', marginTop: '0.5rem', opacity: 0.7}}>
                    Start shopping to see your orders here!
                  </p>
                </div>
              ) : (
                orders
                  .filter(order => {
                    if (orderFilter === "all") return true;
                    return order.status === orderFilter;
                  })
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map((order, index) => (
                    <div key={order.key} className="order-card" style={{animationDelay: `${index * 0.1}s`}}>
                      <div className="order-header">
                        <h3>Order #{order.key}</h3>
                        <span className={`status-badge ${order.status}`}>
                          {order.status}
                        </span>
                      </div>
                      <div className="order-details">
                        <div className="order-info">
                          <p><strong>Total:</strong> ₹{order.total}</p>
                          <p><strong>Date:</strong> {new Date(order.createdAt).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}</p>
                          {order.paymentId && (
                            <p><strong>Payment ID:</strong> {order.paymentId}</p>
                          )}
                          {order.shiprocket_order_id && (
                            <p><strong>📦 Shiprocket Order:</strong> {order.shiprocket_order_id}</p>
                          )}
                          {order.shiprocket_shipment_id && (
                            <p><strong>🚚 Shipment ID:</strong> {order.shiprocket_shipment_id}</p>
                          )}
                          {order.shiprocket_awb && (
                            <p><strong>📄 AWB Number:</strong> {order.shiprocket_awb}</p>
                          )}
                          {order.shiprocket_courier && (
                            <p><strong>🚛 Courier:</strong> {order.shiprocket_courier}</p>
                          )}

                        </div>
                        <div className="order-items">
                          <h4>Items:</h4>
                          <ul>
                            {order.items?.map((item, index) => (
                              <li key={index}>
                                <span>{item.name}</span>
                                <span style={{fontWeight: '600', color: '#00b5ad'}}>
                                  Qty: {item.quantity} - ₹{item.price}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        {/* Button to fetch latest order details */}
                        {order.shiprocket_order_id && (
                          <div style={{ margin: '16px 0', textAlign: 'center' }}>
                            <button 
                              onClick={() => fetchAndUpdateOrderDetails(order.shiprocket_order_id, order.key)}
                              style={{
                                backgroundColor: '#17a2b8',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                marginBottom: '8px'
                              }}
                            >
                              🔄 Fetch Latest AWB
                            </button>
                            <small style={{ display: 'block', color: '#666', fontSize: '11px' }}>
                              Click to fetch latest AWB number and tracking details
                            </small>
                          </div>
                        )}
                        
                        {order.shiprocket_order_id && (
                          <div className="shipment-tracking">
                            <h4>Shipment Tracking:</h4>
                            <ShipmentTracker 
                              orderId={order.shiprocket_order_id} 
                              shipmentId={order.shiprocket_shipment_id}
                            />
                          </div>
                        )}
                        {!order.shiprocket_order_id && order.status === 'paid' && (
                          <div className="shipment-tracking">
                            <h4>Shipment Status:</h4>
                            <p>
                              Shipment creation pending. This usually happens automatically after payment.
                            </p>

                          </div>
                        )}
                      </div>
                    </div>
                  ))
              )}
            </div>
            <div className="refund-policy-note">
              <p><strong>⚠️ Important:</strong> We do not provide refunds or cancellations for orders. Please review your orders carefully.</p>
            </div>
          </div>
        )}

        {activeTab === "settings" && (
          <div className="settings-section">
            <h2>Account Settings</h2>
            <div className="settings-content">
              <div className="setting-item">
                <h3>Privacy & Security</h3>
                <p>Manage your account privacy and security settings.</p>
                <button className="settings-btn">Manage Privacy</button>
              </div>
              <div className="setting-item">
                <h3>Notifications</h3>
                <p>Configure your notification preferences.</p>
                <button className="settings-btn">Notification Settings</button>
              </div>
              <div className="setting-item">
                <h3>Data & Storage</h3>
                <p>Manage your data and storage settings.</p>
                <button className="settings-btn">Data Settings</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
